import { AfterViewInit, Component, OnDestroy, OnInit, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { SPIN_STYLES, SpinInputModel } from 'countable@model';
import { IntegrationService, OauthConnectionType, STATUS } from 'countable@services';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { IntegrationSourceInterface } from '../../../model/eng/integration-source.interface';
import { IntegrationInterface } from '../../../model/integration/integration.interface';
import { OauthKickOffModel } from '../../../model/oauth/oauth-kick-off.model';
import { CaptchaService } from '../../../services/captcha.service';

@Component({
  selector: 'app-accounting-integration',
  templateUrl: './accounting-integration.component.html',
  styleUrls: ['./accounting-integration.component.scss'],
  standalone: false
})
export class AccountingIntegrationComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('disconnectDialog') disconnectDialog: TemplateRef<any>;

  readonly client : any;
  dataLoaded: boolean = false;
  isRedirecting: boolean = false;
  isConnected: boolean = false;
  integrations: IntegrationInterface[] = [];
  integration: IntegrationInterface = null;
  tenantInfo: IntegrationSourceInterface = null;
  selectedOrganization: any;
  organizationsList: any;
  private readonly connectSourceToEngId: number = 0;
  public spinnerConfig: WritableSignal<SpinInputModel> = signal<SpinInputModel>(new SpinInputModel(null, SPIN_STYLES.BLUR_WITH_SPIN));
  private spinCount: number = 0;
  private isDataLoaded: boolean = false;
  private readonly clientFirmId: number = null;

  constructor(private activatedRoute: ActivatedRoute, private dialog: MatDialog,
    private toaster: ToastrService, private integrationService: IntegrationService, private captchaService: CaptchaService) {

    const params = new URLSearchParams(window.location.search);
    if (params.get('source') === 'onboarding') {
      window.opener?.postMessage({type: 'popup-event', data: params.entries().next()}, window.location.origin);
      window.close();
    }

    this.client = JSON.parse((localStorage.getItem('selectedClientObj')));
    this.clientFirmId = this.client?.clientFirm ? this.client?.clientFirm :this.client?.clientfirmId;
    const sourceConnectToEngStatus = localStorage.getItem(STATUS.STORAGE_KEY);
    if (sourceConnectToEngStatus && sourceConnectToEngStatus === STATUS.NO_CONNECTION_TO_SOURCE) {
      const engagementId: number = (+localStorage.getItem('engagementID'));
      this.connectSourceToEngId = engagementId ? engagementId : 0;
    }
  }

  ngOnInit() {
    this.updateSpinnerConfig(true);
    this.integrationService.getAccountingIntegrationByClient(this.clientFirmId).pipe(finalize(() => this.updateSpinnerConfig(false))).subscribe(tenant => {
      this.tenantInfo = tenant;
      this.updateSpinnerConfig(true);
      this.integrationService.getAccountingIntegrationsBasicInfo().pipe(finalize(() => this.updateSpinnerConfig(false))).subscribe(integrations => {
        // get the tenant detail until the result of getConnection comes back!
        this.integrations = Array.from(integrations.values());
        if (tenant && tenant.integrationId) {
          this.isConnected = tenant.isConnected;
          this.integration = integrations.get(tenant.integrationId);
          // if there is a next step like Sage
          if (this.integration.needExtraStepAfterAuth && !tenant.tenant) {
            this.getSageOrganizations();
          }
          // check the latest status of the connection if it is not already disconnected
          if (tenant.isConnected && !(this.integration.needExtraStepAfterAuth && !tenant.tenant)) {
            this.integrationService.checkConnection(tenant.id, tenant.hash).subscribe({
              next: isConnectionActive => {
                this.isConnected = isConnectionActive;
                if (isConnectionActive && this.connectSourceToEngId) {
                  this.triggerTrailBalanceRefresh();
                }
                if (!isConnectionActive) {
                  localStorage.setItem('integratedSuccessfully', 'failed');
                }
              }
            });
          }
          localStorage.setItem('integratedSuccessfully', 'success');
        }

        this.activatedRoute.queryParamMap.subscribe(params => {
          for (const i of this.integrations) {
            if (params.has(i.uiKey)) {
              if (params.get(i.uiKey) === 'true') {
                localStorage.setItem('partnerClientAccountIntegration', 'Success');
              } else {
                this.toaster.error('Could not connect to ' + i.label + ', please contact the support team');
              }
              break;
            }
          }
        });
        this.dataLoaded = true;
      });
    });
  }

  ngAfterViewInit(): void {
    this.captchaService.showCaptchaBanner().then();
  }

  oauthKickOff(i: IntegrationInterface) {
    if (!this.clientFirmId) {
      this.toaster.error('Something went wrong, please refresh the page');
      this.isRedirecting = false;
      return;
    }
    try {
      this.captchaService.getCaptchaToken('accounting').then((captcha) => {
        const data: OauthKickOffModel = {
          client: this.clientFirmId,
          redirectMode: OauthConnectionType.PARTNER_INTEGRATION, // enum in IntegrationOauthStateEntity
          key: i.uiKey,
          captcha: captcha
        };
        this.updateSpinnerConfig(true);
        this.integrationService.oauthProvidersKickOff(data).pipe(finalize(() => this.updateSpinnerConfig(false))).subscribe({
          next: res => {
            if (res.status === 200) {
              this.toaster.success('You will be redirected for authentication in a moment');
              window.open(res.data, '_self');
            } else {
              this.isRedirecting = false;
              res.errors.forEach(error => {
                this.toaster.error(error);
              });
            }
          },
          error: () => {
            this.toaster.error('Could not initiate the oauth procedure, please contact support');
            this.isRedirecting = false;
          }
        });
      });
    } catch (e) {
      console.log(e);
      this.isRedirecting = false
    }
  }

  openDisconnectPopup() {
    this.dialog.open(this.disconnectDialog, { disableClose: true });
  }

  disconnect() {
    this.integrationService.disconnect(this.clientFirmId).subscribe(res => {
      localStorage.removeItem('integratedSuccessfully');
      localStorage.setItem('integratedSuccessfully', 'success');
      if (res.status === 200) {
        localStorage.setItem('partnerClientAccountIntegration', 'Success');
        this.toaster.success('Disconnected from ' + this.integration.label);
      } else {
        this.toaster.error('Facing error disconnecting from ' + this.integration.label + ', please refresh the page');
      }
      this.integrationService.deleteIntegrationSourceByFirm(this.clientFirmId);
      this.tenantInfo = null;
      this.integration = null;
    });
  }

  closeWindow() {
    window.localStorage.removeItem('selectedClientObj');
    window.localStorage.removeItem(STATUS.STORAGE_KEY);
    try {
      window.opener.postMessage({ 'resetIntegration': this.clientFirmId });
    } catch (e) {
      this.toaster.warning('Could not refresh the integration automatically, please refresh the main page');
    }
    window.close();
  }

  getSageOrganizations() {
    this.integrationService.getSageOrganizations(this.clientFirmId).subscribe(res => {
      this.organizationsList = res;
    });
  }

  saveOrganization() {
    const orgDetails = {
      'realm': this.selectedOrganization.realm,
      'source': this.selectedOrganization.source,
      'name': this.selectedOrganization.organization
    };
    this.integrationService.saveSageOrganization(orgDetails).subscribe(response => {
      if (response) {
        this.refreshTenant();
      } else {
        this.toaster.error('Could not save the Sage organization');
      }

    });
  }

  ngOnDestroy() {
    this.captchaService.hideCaptchaBanner();
    window.localStorage.removeItem('selectedClientObj');
  }

  private refreshTenant() {
    this.integrationService.getAccountingIntegrationByClient(this.clientFirmId).subscribe(tenant => {
      this.tenantInfo = tenant;
    });
  }

  private triggerTrailBalanceRefresh() {
    this.integrationService.sourceToEngagementConnection(this.connectSourceToEngId, this.clientFirmId).subscribe({
      next: res => {
        if (res.status === 200 && res.data) {
          localStorage.setItem(STATUS.STORAGE_KEY, STATUS.FETCH_FROM_SOURCE);
        } else {
          res.errors.forEach((error) => {
            this.toaster.error(error);
          });
        }
      }
    });
  }

  private updateSpinnerConfig(value: boolean) {
    value ? this.spinCount++ : (this.spinCount = Math.max(0, this.spinCount - 1));
    const spinner = this.spinnerConfig();
    this.spinnerConfig.set({
      ...spinner,
      isSpinShown: this.spinCount > 0
    });
  }
}
